import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavSidebarLink, NavMenuTitle, NavMenu, NavMenuLinks, BackButton } from 'src/common';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { Paths } from 'src/pages/paths';
import { useLocation } from 'react-router-dom';
import { useBackHref } from 'src/helpers/history';
import { NavSidebar, useNavSidebar } from 'src/shared';
import {
  faUserNinja,
  faBell,
  faBuilding,
  faUsers,
  faShareAlt,
  faCogs,
  faShieldAlt,
  faChartLine,
  faPen
} from '@fortawesome/pro-light-svg-icons';
import {
  faUserNinja as faUserNinjaSolid,
  faBell as faBellSolid,
  faBuilding as faBuildingSolid,
  faUsers as faUsersSolid,
  faShareAlt as faShareAltSolid,
  faCogs as faCogsSolid,
  faShieldAlt as faShieldAltSolid,
  faChartLine as faChartLineSolid,
  faPen as faPenSolid
} from '@fortawesome/pro-solid-svg-icons';
import { isPaidUser, useApp } from 'src/models/auth';
import { Ownerships, ownership } from 'src/models/ownership';

const SettingsNavContent: FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user } = useApp();
  const isPaid = useMemo(() => isPaidUser(user), [user]);
  const companyId = useMemo(() => (isPaidUser(user) ? user.company.id : null), [user]);
  const isAdmin = useMemo(() => isPaidUser(user) && user.isAdmin, [user]);
  const [sidebarIsOpen] = useNavSidebar();

  const isAuthMfaFeature = useFeatureFlag(FeatureFlag.Auth_2FA);
  const isAuthSsoFeature = useFeatureFlag(FeatureFlag.Auth_SSO) || false;
  const isWebhooksFeature = useFeatureFlag(FeatureFlag.Users_Webhooks);
  const isTokensFeature = useFeatureFlag(FeatureFlag.Users_IntegrationToken);
  const isCustomBidFieldsFeature = useFeatureFlag(FeatureFlag.CustomBidFields);

  const employeesAvailable = !!companyId;
  const securityAvailable = (isAuthMfaFeature || isAuthSsoFeature) && isAdmin;
  const webhooksAvailable = isWebhooksFeature && isAdmin;
  const tokensAvailable = isTokensFeature && isAdmin;
  const competitorsAvailable = ownership.name !== Ownerships.Clira;
  const notiicationAvailable = isPaid && ownership.name !== Ownerships.Clira;

  const backHref = useBackHref();
  const backDisabled = useMemo(() => backHref.includes(pathname), [backHref, pathname]);

  return (
    <>
      <NavMenu>
        <NavMenuLinks>
          <BackButton href={backHref} isDisabled={backDisabled} />
        </NavMenuLinks>
      </NavMenu>
      <NavMenu>
        <NavMenuTitle isOpen={sidebarIsOpen}>{t('Navigation.Settings.user')}</NavMenuTitle>
        <NavMenuLinks>
          <NavSidebarLink
            to={{ pathname: Paths.SETTINGS_PAGE_ACCOUNT, state: { from: backHref } }}
            title={t('Navigation.Settings.myProfile')}
            icon={{ defaultIcon: faUserNinja, activeIcon: faUserNinjaSolid }}
            isActive={pathname === Paths.SETTINGS_PAGE_ACCOUNT}
            isExpanded={sidebarIsOpen}
          />
          {notiicationAvailable && (
            <NavSidebarLink
              to={{ pathname: Paths.SETTINGS_PAGE_NOTIFICATIONS, state: { from: backHref } }}
              title={t('Navigation.Settings.notifications')}
              icon={{ defaultIcon: faBell, activeIcon: faBellSolid }}
              isActive={pathname === Paths.SETTINGS_PAGE_NOTIFICATIONS}
              isExpanded={sidebarIsOpen}
            />
          )}
        </NavMenuLinks>
      </NavMenu>
      {isPaid && (
        <NavMenu>
          <NavMenuTitle isOpen={sidebarIsOpen}>{t('Navigation.Settings.organisation')}</NavMenuTitle>
          <NavMenuLinks>
            <NavSidebarLink
              to={{ pathname: Paths.SETTINGS_PAGE_COMPANY_INFO, state: { from: backHref } }}
              title={t('Navigation.Settings.companyProfile')}
              icon={{ defaultIcon: faBuilding, activeIcon: faBuildingSolid }}
              isActive={pathname === Paths.SETTINGS_PAGE_COMPANY_INFO}
              isExpanded={sidebarIsOpen}
            />
            {employeesAvailable && (
              <NavSidebarLink
                to={{ pathname: Paths.SETTINGS_PAGE_EMPLOYEES, state: { from: backHref } }}
                title={t('Navigation.Settings.teammates')}
                icon={{ defaultIcon: faUsers, activeIcon: faUsersSolid }}
                isActive={pathname === Paths.SETTINGS_PAGE_EMPLOYEES}
                isExpanded={sidebarIsOpen}
              />
            )}
            {competitorsAvailable && (
              <NavSidebarLink
                to={{ pathname: Paths.SETTINGS_PAGE_COMPETITORS, state: { from: backHref } }}
                title={t('Navigation.Settings.competitors')}
                icon={{ defaultIcon: faChartLine, activeIcon: faChartLineSolid }}
                isActive={pathname === Paths.SETTINGS_PAGE_COMPETITORS}
                isExpanded={sidebarIsOpen}
              />
            )}
            {webhooksAvailable && (
              <NavSidebarLink
                to={{ pathname: Paths.SETTINGS_PAGE_COMPANY_WEBHOOKS, state: { from: backHref } }}
                title={t('Navigation.Settings.webhooks')}
                icon={{ defaultIcon: faShareAlt, activeIcon: faShareAltSolid }}
                isActive={pathname === Paths.SETTINGS_PAGE_COMPANY_WEBHOOKS}
                isExpanded={sidebarIsOpen}
              />
            )}
            {tokensAvailable && (
              <NavSidebarLink
                to={{ pathname: Paths.SETTINGS_PAGE_COMPANY_TOKENS, state: { from: backHref } }}
                title={t('Navigation.Settings.integrationTokens')}
                icon={{ defaultIcon: faCogs, activeIcon: faCogsSolid }}
                isActive={pathname === Paths.SETTINGS_PAGE_COMPANY_TOKENS}
                isExpanded={sidebarIsOpen}
              />
            )}
            {isCustomBidFieldsFeature && (
              <NavSidebarLink
                to={{ pathname: Paths.SETTINGS_PAGE_CUSTOM_BID_FIELDS, state: { from: backHref } }}
                title={t('Navigation.Settings.customBidFields')}
                icon={{ defaultIcon: faPen, activeIcon: faPenSolid }}
                isActive={pathname === Paths.SETTINGS_PAGE_CUSTOM_BID_FIELDS}
                isExpanded={sidebarIsOpen}
              />
            )}
            {securityAvailable && (
              <NavSidebarLink
                to={{ pathname: Paths.SETTINGS_PAGE_COMPANY_SECURITY, state: { from: backHref } }}
                title={t('Navigation.Settings.security')}
                icon={{ defaultIcon: faShieldAlt, activeIcon: faShieldAltSolid }}
                isActive={pathname === Paths.SETTINGS_PAGE_COMPANY_SECURITY}
                isExpanded={sidebarIsOpen}
              />
            )}
          </NavMenuLinks>
        </NavMenu>
      )}
    </>
  );
};

export const SettingsNav: FC = () => (
  <NavSidebar>
    <SettingsNavContent />
  </NavSidebar>
);

export default SettingsNav;
